import axios, { Method } from 'axios';
import { RecordListParameter, RecordListPayload, RecordListTypes, ReducerActionPayload, useDataContext } from 'context/DataStoreContext';
import { getToken, resetToken } from 'model/Auth';
import { Role, normalizeRole } from 'model/Role';
import { User } from 'model/User';
import { Partner } from 'model/Partner';
import { Ledger, LedgerMember } from 'model/Ledger';
import { Report, ReportMemberUser, ReportMemberPartner } from 'model/Report';
import { Contractor } from 'model/Contractor';
import { WorkProcess } from 'model/WorkProcess';
import { WorkResource } from 'model/WorkResource';
import { ResourceParam } from 'model/Resource';
import { LineRelation } from 'model/Relation';

const apiUrl = process.env.REACT_APP_API_ENDPOINT || '';

type ApiResponseKey<S extends string> = S extends `${infer T}${infer U}${infer V}`
  ? U extends Capitalize<U>
    ? `${T}${U}` extends `${number}${number}`
      ? `${T}${ApiResponseKey<`${U}${V}`>}`
      : `${T}_${Lowercase<U>}${ApiResponseKey<V>}`
    : `${T}${ApiResponseKey<`${U}${V}`>}`
  : S extends `${infer W}${infer X}`
    ? X extends Capitalize<X>
      ? `${W}${X}` extends `${number}${number}`
        ? `${W}${X}`
        : X extends ''
          ? S
          : `${W}_${Lowercase<X>}`
      : `${W}${X}`
    : S;

type ApiResponseData<T extends object | undefined> = {
  [K in keyof T as `${ApiResponseKey<string & K>}`]:
    T[K] extends Array<infer S> | undefined
      ? S extends object
        ? ApiResponseData<S>[]
        : T[K]
      : T[K] extends object | undefined
        ? ApiResponseData<T[K]>
        : T[K];
};

export type RecordListResponse<T extends keyof RecordListTypes> = {
  list: RecordListTypes[T]['type'][];
  page: number;
  lastPage: number;
};

export type FailedAuthorizationResponse = Required<{ error: 'failed_authorize' }>;

export type FailedAuthorizationExeption<T> = FailedAuthorizationResponse & { response: T };

export const isFailedAuthorizationResponse = (response: any): response is FailedAuthorizationResponse =>
  response?.error === 'failed_authorize';

export const isFailedAuthorizationException = (error: any): error is FailedAuthorizationExeption<any> =>
  error?.error === 'failed_authorize' && 'response' in error;

/**
 * API通信の結果型
 * ok: true  → 正常レスポンス（data にレスポンス内容）
 * ok: false → 想定内エラー（apiError にエラー種別）
 * 想定外エラー（5xx・ネットワーク障害等）は例外としてthrowされる。
 *
 * 今後 apiError に 'validation_error' | 'not_found' | 'forbidden' 等を追加予定。
 */
export type ApiErrorResponse =
  | { error: 'failed_authorize' }
  | { error: 'duplicate_report'; existingId: number };

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; apiError: ApiErrorResponse };

const toApiRequestKey = (key: string) =>
  key
    .replace(/([A-Z]+)/g, '_$1')
    .replace(/([a-zA-Z])([0-9])/g, '$1_$2')
    .toLowerCase();

const toApiPayload = <T extends { [key: string]: any }>(payload: T): ApiResponseData<T> =>
  Object.keys(payload).reduce((apiPayload, key) => ({
    ...apiPayload,
    [toApiRequestKey(key)]:
      Array.isArray(payload[key])
        ? payload[key].map(
          (value) => value && (typeof value === 'object')
            ? toApiPayload(value)
            : key === 'with'
              ? toApiRequestKey(value)
              : value
        )
        : payload[key] && (typeof payload[key] === 'object')
          ? toApiPayload(payload[key])
          : payload[key],
  }), {}) as ApiResponseData<T>;

const toModelKey = (key: string) =>
  (
    ([first, ...nexts]) =>
      first + nexts.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join('')
  )(key.split('_'));

const toModel = <T extends { [key: string]: any }>(payload: ApiResponseData<T>): T =>
  Object.keys(payload).reduce((apiPayload, key) => ({
    ...apiPayload,
    [toModelKey(key)]:
      Array.isArray(payload[key])
        ? payload[key].map((value: any) => value && (typeof value === 'object') ? toModel(value) : value)
        : payload[key] && (typeof payload[key] === 'object')
          ? toModel(payload[key])
          : payload[key],
  }), {}) as T;

const endpoints: { [key in keyof RecordListTypes]: { get: string, gets: string } } = {
  user: { get: 'user', gets: 'users' },
  partner: { get: 'partner', gets: 'partners' },
  ledger: { get: 'ledger', gets: 'ledgers' },
  schedule: { get: 'ledger', gets: 'ledgers' },
  report: { get: 'report', gets: 'reports' },
  contractor: { get: 'contractor', gets: 'contractors' },
  workProcess: { get: 'work_process', gets: 'work_processes' },
  workResource: { get: 'work_resource', gets: 'work_resources' },
  lineRelation: { get: 'line_relation', gets: 'line_relations' },
  role: { get: 'role', gets: 'roles' },
};

type UserPayload = { roleId: User['roleId'] | User['roleId'][], with: ('role' | 'relation')[] };
type LedgerPayload = { with: ('leader' | 'safetyOfficer' | 'member' | 'primeContractor' | 'member' | 'report' | 'resource' | 'cost')[] };
type ReportPayload = { with: ('ledger' | 'leader' | 'safetyOfficer' | 'member' | 'workProcess' | 'resource' | 'cost')[] };
type ReportListPayload = ReportPayload & { dateCode: number, forEdit?: boolean };
type LineRelationPayload = { with: ('user')[] };

export const request = async <Response extends { [key: string]: any }>(
  endpoint: string,
  method: Method,
  dataPayload?: FormData | {} | null,
  headerPayload?: {} | null,
  defaultResponse?: Response
): Promise<Response | undefined> => {
  const neededAuth = !['login', 'logout'].includes(endpoint);
  const withCredentials = endpoint === 'login';
  const headers = (headerPayload || neededAuth !== false) && {
    ...(headerPayload || {}),
    ...(neededAuth !== false ? { Authorization: `Bearer ${getToken()}` } : {}),
  };
  const config = {
    url: `${apiUrl}/${endpoint}`,
    method,
    withCredentials,
    ...(method === 'get' ? { params: dataPayload } : {}),
    ...(method !== 'get' ? { data: dataPayload } : {}),
    ...(headers ? { headers } : {}),
  };

  const response = await axios.request<ApiResponseData<Response> | FailedAuthorizationResponse | undefined>(config);
  if (isFailedAuthorizationResponse(response.data)) {
    throw { ...response.data, response: defaultResponse };
  }

  return response.data ? toModel<Response>(response.data) : defaultResponse;
};

export const useApi = () => {
  const { state, dispatch } = useDataContext();

  const requestAndCheckAuth = async <Response extends { [key: string]: any }>(...[
    endpoint,
    method,
    dataPayload,
    headerPayload,
    defaultResponse,
  ]: Parameters<typeof request<Response>>): Promise<ApiResult<Response | undefined>> => {
    try {
      const data = await request<Response>(endpoint, method, dataPayload, headerPayload, defaultResponse);

      return { ok: true, data };
    } catch (error) {
      if (isFailedAuthorizationException(error)) {
        dispatch?.({ type: 'RESET_AUTH', payload: { failedAlert: true } });
        resetToken();

        return { ok: false, apiError: { error: 'failed_authorize' } };
      }

      // 409 duplicate_report
      const axiosError = error as any;
      if (axiosError?.response?.status === 409 && axiosError?.response?.data?.error === 'duplicate_report') {
        return { ok: false, apiError: { error: 'duplicate_report', existingId: toModel<{ existingId: number }>(axiosError.response.data).existingId } };
      }

      // 想定外エラー（AxiosError 等）は呼び出し元に伝播させる
      throw error;
    }
  };

  const getById = async <T extends keyof RecordListTypes>(
    recordType: T,
    id: number,
    payload?: Partial<{ [key: string]: string | number | string[] | number[] }>,
  ): Promise<RecordListTypes[T]['type'] | null> => {
    try {
      const result = await requestAndCheckAuth<{ record: RecordListTypes[T]['type'] }>(`${endpoints[recordType].get}/${id}`, 'get', toApiPayload(payload || {}));
      if (!result.ok) return null;

      return result.data?.record || null;
    } catch {
      return null;
    }
  };

  const getPageList = async <T extends keyof RecordListTypes>(
    recordType: T,
    payload?: Partial<RecordListPayload>,
    updateState?: boolean
  ): Promise<RecordListTypes[T]['type'][]> => {
    const { conditions: payloadConditions, ...restPayload } = payload || {};
    const apiPayload = toApiPayload({
      ...payloadConditions,
      ...restPayload,
      ...(
        payload?.sorts
          ? { sorts: payload?.sorts.map(({ key, order }) => ({ key: toApiRequestKey(key), order: order ? 'desc' : 'asc' })) }
          : {}
      ),
    });
    try {
      const result = await requestAndCheckAuth<RecordListResponse<T>>(endpoints[recordType].gets, 'get', apiPayload);
      if (!result.ok) return [];

      if (updateState) {
        const responsedPayload: RecordListParameter<T> = {
          list: result.data?.list || [],
          perPage: result.data?.lastPage || 1,
          conditions: payload?.conditions || {},
          sorts: payload?.sorts || [],
          limit: payload?.limit || 1,
          page: result.data?.page || payload?.page || 1,
        };
        const actionPayload = { type: recordType, payload: responsedPayload } as ReducerActionPayload['SET_PAGE_LIST'];
        dispatch?.({ type: 'SET_PAGE_LIST', payload: actionPayload } );
      }

      return result.data?.list || [];
    } catch {
      return [];
    }
  };

  return {
    auth: {
      reference: async () => {
        try {
          const result = await requestAndCheckAuth<{ user: User }>('auth', 'get');
          if (!result.ok) return null;

          return result.data?.user || null;
        } catch {
          return null;
        }
      },
      login: async (account: string | null, password: string | null): Promise<ApiResult<{ user: User; token: string } | undefined>> =>
        requestAndCheckAuth<{ user: User; token: string }>('login', 'post', { account, password }),
      logout: async () => {
        try {
          await requestAndCheckAuth<{}>('logout', 'post');
        } catch {
          // ログアウトは失敗しても無視
        }
      },
      changePassword: async (password: string): Promise<ApiResult<boolean>> => {
        const result = await requestAndCheckAuth<{ result: string }>('password', 'post', { password });
        if (!result.ok) return result;

        return { ok: true, data: result.data?.result === 'success' };
      },
    },
    role: {
      get: async (id: number): Promise<Role | null> => {
        try {
          const result = await requestAndCheckAuth<{ record: Role }>(`role/${id}`, 'get');
          if (!result.ok) return null;
          const record = result.data?.record;

          return record ? normalizeRole(record) : null;
        } catch {
          return null;
        }
      },
      getsByOwn: async (): Promise<Role[]> => {
        try {
          const result = await requestAndCheckAuth<{ list: Role[] }>('roles', 'get');
          if (!result.ok) return [];

          return (result.data?.list || []).map(normalizeRole);
        } catch {
          return [];
        }
      },
      paginate: async (payload?: Partial<RecordListPayload>): Promise<Role[]> => {
        // getPageList が perPage 等を含めて dispatch した後、permissions を normalize した list で上書きする
        const rawRecords = await getPageList('role', payload, true);
        const records = rawRecords.map(normalizeRole);
        dispatch?.({
          type: 'SET_PAGE_LIST',
          payload: { type: 'role', payload: { list: records } },
        });

        return records;
      },
      update: async (role: Role): Promise<ApiResult<boolean | Role | undefined>> => {
        const apiPayload = toApiPayload({ role });
        if (role.id) {
          const result = await requestAndCheckAuth<{ result: boolean }>(`role/${role.id}`, 'post', apiPayload);
          if (!result.ok) return result;

          return { ok: true, data: result.data?.result };
        }
        const result = await requestAndCheckAuth<{ record: Role }>('role', 'put', apiPayload);
        if (!result.ok) return result;

        return { ok: true, data: result.data?.record ? normalizeRole(result.data.record) : undefined };
      },
      delete: (id: Role['id']): Promise<ApiResult<{ result: boolean } | undefined>> =>
        requestAndCheckAuth<{ result: boolean }>(`role/${id}`, 'delete'),
    },
    client: {
      getLineCodeImageUrl: async (clientId?: number) => {
        const { lineCodeImageUrl } = state?.client || {};
        if (lineCodeImageUrl) {
          return lineCodeImageUrl;
        }

        try {
          const result = await requestAndCheckAuth<{ url: string }>('client/line_code_image_url', 'get', { clientId });
          if (!result.ok) return null;

          dispatch?.({ type: 'SET_CLIENT', payload: { lineCodeImageUrl: result.data?.url || '' } });

          return result.data?.url || null;
        } catch {
          return null;
        }
      },
    },
    user: {
      get: async (id: number, payload?: Partial<UserPayload>) => getById('user', id, payload),
      getsByOwn: (payload?: Partial<RecordListPayload & UserPayload>) => getPageList('user', payload),
      paginate: (payload?: Partial<RecordListPayload & UserPayload>) => getPageList('user', payload, true),
      update: async (
        user: User,
        payload?: Partial<{ password: string; customRolePermissions: Role['permissions'] }>,
      ): Promise<ApiResult<boolean | User | undefined>> => {
        const apiPayload = toApiPayload({ user, ...payload || {} });
        if (user.id) {
          const result = await requestAndCheckAuth<{ result: boolean }>(`user/${user.id}`, 'post', apiPayload);
          if (!result.ok) return result;

          return { ok: true, data: result.data?.result };
        }
        const result = await requestAndCheckAuth<{ record: User }>('user', 'put', apiPayload);
        if (!result.ok) return result;

        return { ok: true, data: result.data?.record };
      },
      delete: (id: User['id']): Promise<ApiResult<{ result: boolean } | undefined>> =>
        requestAndCheckAuth<{ result: boolean }>(`user/${id}`, 'delete'),
    },
    partner: {
      get: (id: number): Promise<Partner | null> => getById('partner', id),
      getsByOwn: (payload?: Partial<RecordListPayload>) => getPageList('partner', payload),
      paginate: (payload?: Partial<RecordListPayload>) => getPageList('partner', payload, true),
      update: async (partner: Partner): Promise<ApiResult<boolean | Partner | undefined>> => {
        if (partner.id) {
          const result = await requestAndCheckAuth<{ result: boolean }>(`partner/${partner.id}`, 'post', toApiPayload({ partner }));
          if (!result.ok) return result;

          return { ok: true, data: result.data?.result };
        }
        const result = await requestAndCheckAuth<{ record: Partner }>('partner', 'put', toApiPayload({ partner }));
        if (!result.ok) return result;

        return { ok: true, data: result.data?.record };
      },
      delete: (id: Partner['id']): Promise<ApiResult<{ result: boolean } | undefined>> =>
        requestAndCheckAuth<{ result: boolean }>(`partner/${id}`, 'delete'),
    },
    ledger: {
      get: (id: number, payload?: Partial<LedgerPayload>) => getById('ledger', id, payload),
      getsByOwn: (payload?: Partial<RecordListPayload & LedgerPayload>) => getPageList('ledger', payload),
      paginate: (payload?: Partial<RecordListPayload & LedgerPayload>) => getPageList('ledger', payload, true),
      update: async (
        ledger: Ledger,
        leaderIds: number[],
        safetyOfficerIds: number[],
        members?: LedgerMember[],
        resources?: { file: File, summary?: string }[],
        deletingResources?: ResourceParam['indexName'][]
      ): Promise<ApiResult<Ledger | null>> => {
        const payload = toApiPayload({ ledger, leaderIds, safetyOfficerIds, ...(members ? { members } : {}) });
        const ledgerRequest = ledger.id
          ? requestAndCheckAuth<{ ledger: Ledger }>(`ledger/${ledger.id}`, 'post', payload)
          : requestAndCheckAuth<{ ledger: Ledger }>('ledger', 'put', payload);
        const resourceRequest: Promise<unknown> = (resources?.length || deletingResources?.length)
          ? (() => {
            const resourceDataPayload = new FormData();
            resourceDataPayload.append('type', 'ledger');
            resourceDataPayload.append('resource_code', ledger.resourceCode);
            (resources || []).forEach((resource, index) => {
              resourceDataPayload.append(`resource[${index}]`, resource.file);
              if (resource.summary) {
                resourceDataPayload.append(`resource_detail[${index}][summary]`, resource.summary);
              }
            });
            (deletingResources || []).forEach((indexName, index) => {
              resourceDataPayload.append(`delete_resource[${index}]`, indexName);
            });

            return requestAndCheckAuth<{}>('resource', 'post', resourceDataPayload);
          })()
          : new Promise<void>((resolve) => resolve());
        const [recordResult] = await Promise.all([ledgerRequest, resourceRequest]);
        if (!recordResult.ok) return recordResult;

        return { ok: true, data: recordResult.data?.ledger || null };
      },
      delete: (id: Ledger['id']): Promise<ApiResult<{ result: boolean } | undefined>> =>
        requestAndCheckAuth<{ result: boolean }>(`ledger/${id}`, 'delete'),
      assign: async (
        id: number,
        datetime: number,
        userIds: User['id'][], partnerIds: Partner['id'][]
      ): Promise<ApiResult<Ledger | null>> => {
        const result = await requestAndCheckAuth<{ ledger: Ledger }>(`ledger/${id}/assign`, 'post', { user_ids: userIds, partner_ids: partnerIds, date: datetime });
        if (!result.ok) return result;

        return { ok: true, data: result.data?.ledger || null };
      },
    },
    schedule: {
      paginate: (payload?: Partial<RecordListPayload & LedgerPayload>) => getPageList('schedule', payload, true),
    },
    report: {
      get: (id: number, payload?: Partial<ReportPayload>) => getById('report', id, payload),
      getsByOwn: (payload?: Partial<RecordListPayload & ReportListPayload>) => getPageList('report', payload),
      paginate: (payload?: Partial<RecordListPayload>) => getPageList('report', payload, true),
      update: async (
        report: Report,
        memberUsers: ReportMemberUser[],
        memberPartners: ReportMemberPartner[],
        resources?: { file: File, summary: string }[],
        deletingResources?: ResourceParam['indexName'][]
      ): Promise<ApiResult<Report | null>> => {
        const reportPayload = {
          report: toApiPayload(report),
          member_users: memberUsers.map((member) => toApiPayload(member)),
          member_partners: memberPartners.map((member) => toApiPayload(member)),
        };
        const reportRequest = report.id
          ? requestAndCheckAuth<{ report: Report }>(`report/${report.id}`, 'post', reportPayload)
          : requestAndCheckAuth<{ report: Report }>('report', 'put', reportPayload);
        const resourceRequest: Promise<unknown> = (resources?.length || deletingResources?.length)
          ? (() => {
            const resourceDataPayload = new FormData();
            resourceDataPayload.append('type', 'report');
            resourceDataPayload.append('resource_code', report.resourceCode);
            (resources || []).forEach((resource, index) => {
              resourceDataPayload.append(`resource[${index}]`, resource.file);
              resourceDataPayload.append(`resource_detail[${index}][summary]`, resource.summary);
            });
            (deletingResources || []).forEach((indexName, index) => {
              resourceDataPayload.append(`delete_resource[${index}]`, indexName);
            });

            return requestAndCheckAuth<{}>('resource', 'post', resourceDataPayload);
          })()
          : new Promise<void>((resolve) => resolve());
        const [recordResult] = await Promise.all([reportRequest, resourceRequest]);
        if (!recordResult.ok) return recordResult;

        return { ok: true, data: recordResult.data?.report || null };
      },
      delete: (id: Report['id']): Promise<ApiResult<{ result: boolean } | undefined>> =>
        requestAndCheckAuth<{ result: boolean }>(`report/${id}`, 'delete'),
    },
    contractor: {
      get: (id: number): Promise<Contractor | null> => getById('contractor', id),
      getsByOwn: (payload?: Partial<RecordListPayload>) => getPageList('contractor', payload),
      paginate: (payload?: Partial<RecordListPayload>) => getPageList('contractor', payload, true),
      update: async (contractor: Contractor): Promise<ApiResult<Contractor | null>> => {
        const result = contractor.id
          ? await requestAndCheckAuth<{ contractor: Contractor }>(`contractor/${contractor.id}`, 'post', toApiPayload({ contractor }))
          : await requestAndCheckAuth<{ contractor: Contractor }>('contractor', 'put', toApiPayload({ contractor }));
        if (!result.ok) return result;

        return { ok: true, data: result.data?.contractor || null };
      },
      delete: (id: Contractor['id']): Promise<ApiResult<{ result: boolean } | undefined>> =>
        requestAndCheckAuth<{ result: boolean }>(`contractor/${id}`, 'delete'),
    },
    workProcess: {
      getsByOwn: (payload?: Partial<RecordListPayload>) => getPageList('workProcess', payload),
      paginate: (payload?: Partial<RecordListPayload>) => getPageList('workProcess', payload, true),
      updates: async (payload: Partial<{
        createList: WorkProcess[];
        updateList: WorkProcess[];
        deleteList: WorkProcess['id'][];
      }>): Promise<ApiResult<boolean | undefined>> => {
        const result = await requestAndCheckAuth<{ result: boolean }>('work_processes', 'post', toApiPayload(payload || {}));
        if (!result.ok) return result;

        return { ok: true, data: result.data?.result };
      },
    },
    workResource: {
      getsByOwn: (payload?: Partial<RecordListPayload & { types: WorkResource['type'][] }>) => getPageList('workResource', payload),
      paginate: (payload?: Partial<RecordListPayload & { types: WorkResource['type'][] }>) => getPageList('workResource', payload, true),
      updates: async (payload: Partial<{
        createList: WorkResource[];
        updateList: WorkResource[];
        deleteList: WorkResource['id'][];
      }>): Promise<ApiResult<boolean | undefined>> => {
        const result = await requestAndCheckAuth<{ result: boolean }>('work_resources', 'post', toApiPayload(payload || {}));
        if (!result.ok) return result;

        return { ok: true, data: result.data?.result };
      },
    },
    lineRelation: {
      get: (id: number, payload?: Partial<LineRelationPayload>) => getById('lineRelation', id, payload),
      paginate: (payload?: Partial<RecordListPayload & LineRelationPayload>) => getPageList('lineRelation', payload, true),
      relate: (id: LineRelation['id'], userId: User['id']): Promise<ApiResult<{} | undefined>> =>
        requestAndCheckAuth<{}>(`line_relation/${id}/relate`, 'post', toApiPayload({ userId })),
    },
  };
};
